// /branschstudier/formatbenchmark-2026 — linkable asset nr 2 (authority).
// Ledtider, längder, versioner och volymfördelning per filmformat.
// Schema: Report + Dataset + Claim per siffra + speakable + breadcrumbs.

import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorByline } from "@/components/site/AuthorByline";
import { EDITORIAL_PEOPLE, authorId, authorPath } from "@/content/editorial";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, claim, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, BRAND, ORG_ID, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import {
  BENCHMARKS,
  BENCHMARK_LICENSE,
  BENCHMARK_LICENSE_URL,
  BENCHMARK_METHOD,
  BENCHMARK_PATH,
  BENCHMARK_PUBLISHED,
  BENCHMARK_STATS,
  BENCHMARK_TITLE,
} from "@/lib/data/formatbenchmark-2026";

const PATH = BENCHMARK_PATH;
const TITLE = BENCHMARK_TITLE;
const DESCRIPTION =
  "Öppen benchmark för svensk filmproduktion 2026: ledtid från brief till leverans, antal inspelningsdagar, versioner per film och volymfördelning per format. Fri att citera under CC BY 4.0 — ladda ner som CSV.";
const CSV_URL = "/api/public/formatbenchmark-2026.csv";

export const Route = createFileRoute("/branschstudier_/formatbenchmark-2026")({
  head: () => {
    const report = {
      "@context": "https://schema.org",
      "@type": "Report",
      name: TITLE,
      headline: TITLE,
      description: DESCRIPTION,
      datePublished: BENCHMARK_PUBLISHED,
      dateModified: BENCHMARK_PUBLISHED,
      inLanguage: "sv-SE",
      url: abs(PATH),
      mainEntityOfPage: abs(PATH),
      license: BENCHMARK_LICENSE_URL,
      author: {
        "@type": "Person",
        "@id": authorId("mattias"),
        name: EDITORIAL_PEOPLE.mattias.name,
        jobTitle: EDITORIAL_PEOPLE.mattias.role,
        url: `https://fenny.se${authorPath("mattias")}`,
        sameAs: EDITORIAL_PEOPLE.mattias.linkedin ? [EDITORIAL_PEOPLE.mattias.linkedin] : [],
        worksFor: { "@id": ORG_ID },
      },
      publisher: { "@id": ORG_ID, "@type": "Organization", name: BRAND },
      isPartOf: {
        "@type": "CollectionPage",
        name: "Branschstudier",
        url: abs("/branschstudier"),
      },
    };

    const dataset = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: TITLE,
      description:
        "Ledtider, inspelningsdagar, versioner och volymandel per filmformat i svensk filmproduktion 2024–2026.",
      url: abs(PATH),
      keywords: [
        "filmproduktion",
        "ledtid",
        "benchmark",
        "format",
        "Sverige",
        "2026",
      ],
      temporalCoverage: "2024/2026",
      spatialCoverage: "Sverige",
      creator: { "@id": ORG_ID },
      author: { "@id": authorId("mattias") },
      license: BENCHMARK_LICENSE_URL,
      measurementTechnique: `Aggregering av ${BENCHMARK_METHOD.sampleProductions} levererade produktioner, ${BENCHMARK_METHOD.measure}.`,
      variableMeasured: [
        "Ledtid brief till leverans (arbetsdagar)",
        "Inspelningsdagar per produktion",
        "Antal versioner per film",
        "Volymandel per format (procent)",
      ],
      distribution: [
        {
          "@type": "DataDownload",
          name: "Formatbenchmark 2026 (CSV)",
          encodingFormat: "text/csv",
          contentUrl: abs(CSV_URL),
        },
      ],
    };

    const claimLds = BENCHMARK_STATS.map((s) =>
      claim({
        text: s.text,
        path: PATH,
        datePublished: BENCHMARK_PUBLISHED,
        about: s.about,
      }),
    );

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Branschstudier", path: "/branschstudier" },
      { name: "Formatbenchmark 2026", path: PATH },
    ]);

    const speakableLd = { ...speakable([".lede", ".key-stat"]), url: abs(PATH) };

    return {
      meta: [
        { title: "Formatbenchmark film 2026 — öppen data | Fenny" },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: SOCIAL_IMAGE_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: SOCIAL_IMAGE_URL },
      ],
      links: [
        { rel: "canonical", href: abs(PATH) },
        { rel: "license", href: BENCHMARK_LICENSE_URL },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/branschstudier/formatbenchmark-2026"})) },
        { type: "application/ld+json", children: JSON.stringify(report) },
        { type: "application/ld+json", children: JSON.stringify(dataset) },
        ...claimLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        { type: "application/ld+json", children: JSON.stringify(speakableLd) },
      ],
    };
  },
  component: BenchmarkPage,
});

function BenchmarkPage() {
  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-12">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            Branschstudie · Publicerad {BENCHMARK_PUBLISHED} · {BENCHMARK_LICENSE}
          </p>
          <h1 className="text-4xl font-semibold sm:text-5xl">{TITLE}</h1>
          <p className="lede text-lg text-muted-foreground">{DESCRIPTION}</p>
          <AuthorByline authorKey="mattias" date={BENCHMARK_PUBLISHED} reviewedDate={BENCHMARK_PUBLISHED} />
          <div className="flex flex-wrap gap-3">
            <a
              href={CSV_URL}
              className="inline-flex items-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
              download
            >
              Ladda ner benchmarken (CSV)
            </a>
            <Button to="/branschstudier/prisindex-2026" variant="outline">
              Se prisindexet
            </Button>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Nyckeltal — citerbara</h2>
          <ul className="space-y-4">
            {BENCHMARK_STATS.map((s) => (
              <li key={s.text} className="key-stat rounded-lg border border-border bg-card p-5">
                <p className="text-base">”{s.text}”</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Ämne: {s.about} · Källa: Fenny formatbenchmark, {BENCHMARK_PUBLISHED}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-5xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Benchmark per format</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Format</th>
                  <th className="px-4 py-3">Längd</th>
                  <th className="px-4 py-3">Ledtid (arbetsdagar)</th>
                  <th className="px-4 py-3">Inspelningsdagar</th>
                  <th className="px-4 py-3">Versioner</th>
                  <th className="px-4 py-3">Volymandel</th>
                </tr>
              </thead>
              <tbody>
                {BENCHMARKS.map((b) => (
                  <tr key={b.format} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{b.format}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.length}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {b.leadMin}–{b.leadMax}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{b.shootDays}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.versions}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.volumeShare} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Ledtid räknas från godkänd brief till levererad master, inklusive två
            återkopplingsrundor.
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Metod</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Urval:</strong>{" "}
              {BENCHMARK_METHOD.sampleProductions} levererade produktioner.
            </li>
            <li>
              <strong className="text-foreground">Period:</strong> {BENCHMARK_METHOD.period}.
            </li>
            <li>
              <strong className="text-foreground">Mått:</strong> {BENCHMARK_METHOD.measure}.
            </li>
            <li>
              <strong className="text-foreground">Felmarginal:</strong>{" "}
              {BENCHMARK_METHOD.marginOfError}.
            </li>
            <li>
              <strong className="text-foreground">Exkluderar:</strong>{" "}
              {BENCHMARK_METHOD.excludes}.
            </li>
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Använd datan — så här citerar du</h2>
          <p className="text-muted-foreground">
            Benchmarken är öppen under {BENCHMARK_LICENSE}. Publicera, bearbeta och
            visualisera fritt, även kommersiellt, mot källangivelse med klickbar länk.
          </p>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm font-medium">Rekommenderad källhänvisning</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Fenny (2026). <em>{TITLE}</em>. Hämtad från{" "}
              <Link to={PATH} className="underline">
                {abs(PATH)}
              </Link>
              .
            </p>
          </div>
          <p className="text-muted-foreground">
            Kombinera gärna med{" "}
            <Link to="/branschstudier/prisindex-2026" className="underline">
              prisindexet
            </Link>
            ,{" "}
            <Link to="/priser" className="underline">
              Fennys prislista
            </Link>{" "}
            och{" "}
            <Link to="/redaktionen" className="underline">
              redaktionen bakom siffrorna
            </Link>
            .
          </p>
        </div>
      </Section>
    </main>
  );
}
